import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  operations,
  type OperationInput,
  type TransitionOption,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { buttonClassName } from '@/shared/ui/buttonStyles';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DetailTabs } from '@/shared/ui/DetailLayout';
import { Icon } from '@/shared/ui/Icon';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Skeleton, SkeletonText } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { Stepper } from '@/shared/ui/Stepper';
import { useToast } from '@/shared/ui/useToast';

import { OperationAuthorityPanel } from '../creditAuthority/OperationAuthorityPanel';
import { GenerationPanel } from '../generation/GenerationPanel';
import { GuarantorsPanel } from './GuarantorsPanel';
import { IssuancePanel } from './IssuancePanel';
import { AnalysisPanels } from './AnalysisPanels';
import { ChangesHistoryDialog } from './ChangesHistoryDialog';
import { EngineHistoryTab } from './EngineHistoryTab';
import { EsteiraDaOperacao } from './EsteiraDaOperacao';
import { FollowUpTab } from './FollowUpTab';
import { OperationDocumentsTab } from './OperationDocumentsTab';
import { OperationForm } from './OperationForm';
import { QuoteDeclineDialog } from './QuoteDeclineDialog';
import { QuoteSummaryTab } from './QuoteSummaryTab';
import { BrokerTimeline } from './quote/BrokerTimeline';

/** As abas do detalhe da cotacao do OnPoint, nesta ordem (COT-030 a COT-034). */
const abasDaCotacao = [
  { key: 'resumo', title: 'Resumo da cotacao' },
  { key: 'motor', title: 'Historico do motor' },
  { key: 'follow-up', title: 'Follow up' },
  { key: 'documentos', title: 'Documentos' },
] as const;

type AbaDaCotacao = (typeof abasDaCotacao)[number]['key'];

/**
 * `/operacoes/:operationId`, com a visao por perfil (P-NCT): a corretora ve a linha do
 * tempo da cotacao dela, sem os paineis de parecer da seguradora; a seguradora continua
 * com a Operacao 360.
 */
export function OperationDetailPage() {
  const { session } = useAuth();
  return session?.broker_scoped === true ? (
    <BrokerTimeline />
  ) : (
    <InsurerOperationDetail />
  );
}

/**
 * Operacao 360, que e o detalhe da cotacao do OnPoint.
 *
 * A tela reune, em um unico lugar, as partes, os valores, a posicao na esteira, os
 * proximos passos possiveis e o historico. O material legado espalhava esses dados
 * por telas diferentes, e a pergunta "em que pe esta esta operacao" exigia abrir
 * quatro delas.
 *
 * As abas sao as do OnPoint (Resumo da cotacao, Historico do motor, Follow up e
 * Documentos), com a aba aberta no endereco (?aba=). Os quadros que o SeguraCore ja
 * tinha continuam: pareceres, emissao, minuta, CCG e avalistas no Resumo; pendencias,
 * conversa e linha do tempo no Follow up.
 */
function InsurerOperationDetail() {
  const { operationId = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editando, setEditando] = useState(false);
  const [declinando, setDeclinando] = useState(false);
  const [vendoAlteracoes, setVendoAlteracoes] = useState(false);
  const [parametros, setParametros] = useSearchParams();
  const aba: AbaDaCotacao =
    abasDaCotacao.find((item) => item.key === parametros.get('aba'))?.key ?? 'resumo';

  function abrirAba(chave: AbaDaCotacao) {
    const proximos = new URLSearchParams(parametros);
    proximos.set('aba', chave);
    setParametros(proximos, { replace: true });
  }

  const consulta = useQuery({
    queryKey: ['operations', operationId],
    queryFn: ({ signal }) => operations.get(operationId, signal),
    enabled: operationId !== '',
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['operations'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }

  const mover = useMutation({
    mutationFn: ({ stage, reason }: { stage: string; reason?: string }) =>
      operations.transition(operationId, stage, reason),
    onSuccess: (operacao) => {
      toast.success(
        `Operacao movida para ${statusLabel('operacao', operacao.stage).toLowerCase()}.`,
      );
      invalidar();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const atualizar = useMutation({
    mutationFn: (input: OperationInput) => operations.update(operationId, input),
    onSuccess: () => {
      toast.success('Operacao atualizada.');
      setEditando(false);
      invalidar();
    },
  });

  if (consulta.isPending) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-8 w-64" />
        <Card>
          <CardBody>
            <SkeletonText lines={5} />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <div className="flex flex-col gap-5">
        <PageHeader title="Operacao" />
        <Alert tone="danger" title="Nao foi possivel abrir a operacao">
          <div className="flex flex-col gap-3">
            <p>{consulta.error.message}</p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void navigate('/operacoes')}
            >
              Voltar para a lista
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  const operacao = consulta.data;
  const avancos = operacao.transitions.filter((item) => item.stage !== 'declinada');
  const declinio = operacao.transitions.find((item) => item.stage === 'declinada');
  const podeAtualizar = can('operacoes.operacao.update');

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={`Operacao ${operacao.number}`}
        description={operacao.subject}
        meta={
          <>
            <StatusBadge kind="operacao" value={operacao.stage} />
            {operacao.is_overdue ? (
              <Badge tone="danger" dot>
                Prazo vencido
              </Badge>
            ) : null}
            {/* Produto e modalidade sao categorias: a cor so separa uma da outra. */}
            <Badge tone="violet">{operacao.product.label}</Badge>
            <Badge tone="indigo">{operacao.modality.label}</Badge>
          </>
        }
        actions={
          <>
            {/* A hierarquia do cabecalho: navegacao em ghost, consulta em secondary e a
                edicao, a acao do cabecalho, em soft. O primario fica no Proximo passo. */}
            <Link to="/operacoes" className={buttonClassName({ variant: 'ghost' })}>
              Lista de cotacoes
            </Link>
            {/* Acompanhamento da cotacao (P-COT-3): a pagina da linha do tempo e o
                historico de alteracoes ("Ver alteracoes" do OnPoint). */}
            <Link
              to={`/operacoes/${operacao.id}/linha-do-tempo`}
              className={buttonClassName({ variant: 'ghost' })}
            >
              Linha do tempo
            </Link>
            <Button variant="secondary" onClick={() => setVendoAlteracoes(true)}>
              Ver alteracoes
            </Button>
            {podeAtualizar && !operacao.is_terminal ? (
              <Button variant="soft" onClick={() => setEditando(true)}>
                <Icon name="pencil" size={15} />
                Editar
              </Button>
            ) : null}
          </>
        }
      />

      <Card>
        <CardBody>
          <Stepper
            steps={operacao.progression.map((stage) => ({
              key: stage,
              label: statusLabel('operacao', stage),
            }))}
            currentKey={operacao.stage}
            terminated={operacao.stage === 'declinada'}
          />
        </CardBody>
      </Card>

      {operacao.stage === 'declinada' ? (
        <Alert tone="danger" title="Operacao declinada">
          {operacao.decline_reason}
        </Alert>
      ) : null}

      <DetailTabs
        label="Abas da cotacao"
        tabs={[...abasDaCotacao]}
        active={aba}
        onChange={abrirAba}
      />

      {aba === 'resumo' ? (
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="flex flex-col gap-5 lg:col-span-2">
            <QuoteSummaryTab operacao={operacao} />

            <AnalysisPanels
              operationId={operacao.id}
              stage={operacao.stage}
              sugestao={{
                policyholderId: operacao.policyholder.id,
                grupo: operacao.modality_info?.credit_group,
                taxaBps: operacao.applied_rate_bps,
              }}
            />

            {/* Pareceres, alcadas e bloqueios de emissao (C-ALC). */}
            <OperationAuthorityPanel operationId={operacao.id} />

            <IssuancePanel operacao={operacao} />

            <GenerationPanel operationId={operacao.id} kind="minuta" />

            <GenerationPanel operationId={operacao.id} kind="ccg" />

            {operacao.notes ? (
              <Card>
                <CardHeader title="Observacoes" />
                <CardBody>
                  <p className="whitespace-pre-wrap text-sm text-ink-600">
                    {operacao.notes}
                  </p>
                </CardBody>
              </Card>
            ) : null}
          </div>

          <div className="flex flex-col gap-5">
            <Card>
              <CardHeader
                title="Proximo passo"
                description="Os movimentos possiveis vem do servidor."
              />
              <CardBody className="flex flex-col gap-2.5">
                {operacao.is_terminal ? (
                  <p className="text-sm text-ink-500">
                    A operacao chegou a uma etapa terminal e nao aceita novos
                    movimentos.
                  </p>
                ) : (
                  <>
                    {avancos.map((transicao) =>
                      transicao.blocked_reason ? (
                        <MovimentoTravado key={transicao.stage} transicao={transicao} />
                      ) : (
                        <Button
                          key={transicao.stage}
                          loading={mover.isPending}
                          onClick={() => mover.mutate({ stage: transicao.stage })}
                        >
                          {transicao.label}
                        </Button>
                      ),
                    )}

                    {declinio ? (
                      <Button
                        variant="danger"
                        disabled={mover.isPending}
                        onClick={() => setDeclinando(true)}
                      >
                        Declinar operacao
                      </Button>
                    ) : null}

                    {avancos.length === 0 && !declinio ? (
                      <p className="text-sm text-ink-500">
                        {operacao.stage === 'em_emissao'
                          ? 'O proximo passo e emitir a garantia, no quadro de emissao. A apolice e a comissao nascem junto com a mudanca de etapa.'
                          : 'Nenhum movimento disponivel para o seu acesso.'}
                      </p>
                    ) : null}
                  </>
                )}
              </CardBody>
            </Card>

            <EsteiraDaOperacao operacao={operacao} />

            <GuarantorsPanel operationId={operacao.id} />
          </div>
        </div>
      ) : null}

      {aba === 'motor' ? <EngineHistoryTab operationId={operacao.id} /> : null}

      {aba === 'follow-up' ? <FollowUpTab operacao={operacao} /> : null}

      {aba === 'documentos' ? <OperationDocumentsTab operacao={operacao} /> : null}

      <ChangesHistoryDialog
        operationId={operacao.id}
        open={vendoAlteracoes}
        onClose={() => setVendoAlteracoes(false)}
      />

      <Modal
        open={editando}
        onClose={() => setEditando(false)}
        title={`Editar operacao ${operacao.number}`}
        description="A etapa nao muda por aqui: ela tem caminho proprio."
        size="lg"
      >
        <OperationForm
          operation={operacao}
          submitting={atualizar.isPending}
          error={atualizar.error}
          onCancel={() => setEditando(false)}
          onSubmit={(input) => atualizar.mutate(input)}
        />
      </Modal>

      {/* O declinio e a negativa da cotacao (P-COT-3): a mesma transicao, com o
          motivo padronizado e a justificativa (SaveProposalDenialJustification). */}
      <QuoteDeclineDialog
        operationId={operacao.id}
        mode="negativa"
        open={declinando}
        onClose={() => setDeclinando(false)}
      />
    </div>
  );
}

/** O quadro de cada parecer na aba Resumo, para onde o aviso leva. */
const quadroDoParecer: Record<string, { ancora: string; nome: string }> = {
  credito: { ancora: 'parecer-credito', nome: 'Analise de credito' },
  subscricao: { ancora: 'parecer-subscricao', nome: 'Subscricao' },
  juridico: { ancora: 'parecer-juridico', nome: 'Parecer juridico' },
};

/**
 * Movimento que ainda nao passa porque o parecer da etapa nao esta aprovado.
 *
 * Antes o botao aparecia ativo e so o clique dizia que faltava o parecer (03/11/2026):
 * quem aprovara o credito do tomador achava que o botao nao funcionava. O motivo vem do
 * servidor; o botao fica travado e o aviso leva ao quadro do parecer, nesta pagina.
 */
function MovimentoTravado({ transicao }: { transicao: TransitionOption }) {
  const quadro = transicao.requires ? quadroDoParecer[transicao.requires] : undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <Button disabled>{transicao.label}</Button>
      <p className="text-2xs text-ink-500">
        {transicao.blocked_reason}
        {quadro
          ? ` Aprove o quadro ${quadro.nome}, nesta pagina, e depois avance a etapa.`
          : ''}
      </p>
      {quadro ? (
        <Button
          size="sm"
          variant="ghost"
          className="self-start"
          onClick={() =>
            document
              .getElementById(quadro.ancora)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        >
          Ir para {quadro.nome}
        </Button>
      ) : null}
    </div>
  );
}
